<template>
  <div class="login-container">
    <div class="glass-card">
      <img class="company-logo" src="@/assets/ascenix_logo.jpg" alt="Company Logo" />

      <h1 class="title">Smart Sensor Data Management</h1>
      <p class="subtitle">Login to your account</p>

      <div v-if="error" class="error-message">{{ error }}</div>

      <form @submit.prevent="handleEmailPasswordLogin">
        <div class="input-wrapper">
           <input type="text" placeholder="Email" class="input-field" v-model="username" />
        </div>
        <div class="input-wrapper password-container">
          <input :type="showPassword ? 'text' : 'password'" placeholder="Password" class="input-field" v-model="password" />
          <span class="toggle-password" @click="showPassword = !showPassword">
            {{ showPassword ? '👁️' : '👁️‍🗨️' }}
          </span>
        </div>
        <button type="submit" class="login-btn" :disabled="loading">
          {{ loading ? 'Logging in...' : 'Login' }}
        </button>
      </form>
       <button class="google-login-btn" @click="signInWithGoogle" :disabled="loading"> <img class="google-icon" src="@/assets/google_logo.svg" alt="Google" />
        Sign in with Google
      </button>

      <div class="links">
        <router-link to="/forgot-password" class="forgot-password">Forgot Password?</router-link>
        <span class="divider">|</span>
        <router-link to="/register" class="sign-up">Sign Up</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'UserLogin',
  data() {
    return {
      username: '', // Can be username or email based on your backend
      password: '',
      showPassword: false,
      loading: false, // Added for loading state
      error: null, // Added for error message display
    };
  },
  computed: {
    // Removed isLoggedIn from computed as it's less relevant here
    // and might cause issues if accessed before login completes.
    // If needed later, ensure it reacts correctly to login state changes.
  },
  methods: {
    // Map Vuex actions and mutations needed
    ...mapActions(['login', 'fetchUserData']), // Keep 'login', add 'fetchUserData'
    ...mapMutations(['SET_USER_DATA', 'SET_ERROR']), // Import mutation to set user data and potentially error

    // --- Email/Password Login Handler ---
    async handleEmailPasswordLogin() {
      if (!this.username || !this.password) {
        this.error = 'Please enter both username/email and password.';
        return;
      }
      this.loading = true;
      this.error = null; // Clear previous errors

      try {
        // 1. Dispatch action to save token in localStorage
        // Note: Your current 'login' action only saves the token.
        await this.login({
           // Adjust field name if backend expects 'email' instead of 'username'
          emailOrUsername: this.username,
          password: this.password,
        }); // Using await in case it becomes async later

        // 3. Redirect to dashboard
        this.$router.push('/dashboard');

      } catch (err) {
        // --- Error ---
        console.error("Login Error:", err);
        this.error = err?.response?.data?.message || 'Login failed. Please check your credentials or try again later.';
         // Optionally clear token/user state on login failure if needed
         // this.$store.dispatch('logout');
      } finally {
        this.loading = false;
      }
    },
    // --- End Email/Password Login Handler ---

    // --- Google Sign In ---
    signInWithGoogle() {
      // Optionally set loading state?
      // this.loading = true;
      window.location.href = `${API_BASE_URL}/auth/google`;
    },
    // --- End Google Sign In ---
  },
  mounted() {
    // --- Google Callback Handling (Existing Logic) ---
    // Consider moving this to a dedicated /auth/callback route/component
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const errorParam = urlParams.get('error'); // Check for error param from callback

    if (token) {
      // Clear the token from the URL bar for cleanliness
      window.history.replaceState({}, document.title, "/login"); // Or window.location.pathname
      this.login(token); // Dispatch action to save token
      // Fetch user data after getting token from Google callback
       this.fetchUserData().then(() => {
           this.$router.push('/dashboard'); // Redirect after getting token AND user data
       }).catch(fetchErr => {
           console.error("Failed to fetch user data after Google login:", fetchErr);
           this.error = "Logged in via Google, but failed to load user details.";
           // Potentially log the user out again if fetching data is critical
           // this.$store.dispatch('logout');
       });
    } else if (errorParam) {
         // Clear the error from the URL bar
         window.history.replaceState({}, document.title, "/login");
         console.error("Google login error callback:", errorParam);
         this.error = `Google Sign-In failed: ${errorParam}`;
    }
    // --- End Google Callback Handling ---
  },
};
</script>

<style scoped>
/* Styles remain the same, but ensure you have styles for .error-message */
/* Example .error-message style (add to your existing <style>) */
.error-message {
  background-color: rgba(255, 0, 0, 0.2);
  color: #ffdddd;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  border: 1px solid rgba(255, 0, 0, 0.3);
  font-size: 14px;
  word-wrap: break-word; /* Prevent long errors breaking layout */
}

/* Re-paste your existing styles here */
.login-container { display: flex; justify-content: center; align-items: center; height: 100vh; background: linear-gradient(-45deg, #141e30, #243b55, #141e30); background-size: 400% 400%; animation: gradientAnimation 10s ease infinite; }
.glass-card { background: rgba(255, 255, 255, 0.15); backdrop-filter: blur(12px); border-radius: 12px; padding: 40px; text-align: center; box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2); width: 350px; max-width: 100%; }
.company-logo { width: 100px; height: auto; margin-bottom: 15px; }
.title { font-size: 24px; color: #fff; font-weight: bold; margin-bottom: 5px; }
.subtitle { color: rgba(255, 255, 255, 0.8); font-size: 14px; margin-bottom: 20px; }
.input-wrapper { width: 100%; position: relative; margin-bottom: 15px; }
.input-field { width: 100%; max-width: 100%; box-sizing: border-box; padding: 12px; border: none; border-radius: 8px; font-size: 16px; background: rgba(255, 255, 255, 0.2); color: #fff; outline: none; }
.input-field::placeholder { color: rgba(255, 255, 255, 0.7); }
.password-container { position: relative; }
.toggle-password { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); cursor: pointer; font-size: 18px; color: rgba(255, 255, 255, 0.7); }
.login-btn, .google-login-btn { width: 100%; max-width: 100%; padding: 12px; font-size: 16px; font-weight: 500; border: none; border-radius: 8px; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; }
.login-btn { background: #4CAF50; color: white; }
.login-btn:disabled { background-color: #aaa; cursor: not-allowed; }
.login-btn:hover:enabled { transform: scale(1.05); box-shadow: 0 4px 10px rgba(255, 255, 255, 0.3); }
.google-login-btn { display: flex; align-items: center; justify-content: center; background: #fff; color: #333; margin-top: 10px; }
.google-login-btn:disabled { background-color: #eee; cursor: not-allowed; color: #999}
.google-login-btn:hover:enabled { transform: scale(1.05); box-shadow: 0 4px 10px rgba(255, 255, 255, 0.3); }
.google-icon { width: 20px; height: 20px; margin-right: 10px; }
.links { display: flex; justify-content: space-between; margin-top: 15px; font-size: 14px; }
.forgot-password, .sign-up { color: rgba(255, 255, 255, 0.8); text-decoration: none; transition: color 0.3s; }
.forgot-password:hover, .sign-up:hover { color: #4CAF50; }
.divider { color: rgba(255, 255, 255, 0.5); margin: 0 5px;}
@keyframes gradientAnimation { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
</style>